<template>
	<div ref="graphRef" class="graph-tree-builder">
		<div class="row items-center mission-bar" style="width: 100%; margin-bottom: 8px">
			<q-select
				dark
				outlined
				dense
				class="sci-fi-selector"
				style="width: 180px"
				label="Mission"
				v-model="selectedGraphId"
				:options="graphOptions"
				option-value="id"
				option-label="name"
				emit-value
				map-options
				use-input
				fill-input
				hide-selected
				input-debounce="0"
				@filter="filterGraphs"
				@input-value="onGraphInput"
				@new-value="onNewGraph"
			/>
			<!-- mousedown.prevent: keep focus in the q-select so its fill-input
			     doesn't reset the typed name to the selected label before the
			     click lands. -->
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="createGraphFromInput"
				color="green"
				icon="add"
				:isDisabled="!canCreateGraph"
				tooltipTitle="Create mission"
				tooltipDesc="Creates a new mission with the typed name."
				style="margin-left: 6px"
				@mousedown.prevent
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="saveGraphName"
				color="green"
				icon="save"
				:isDisabled="!canRenameGraph"
				tooltipTitle="Rename mission"
				tooltipDesc="Renames the selected mission to the typed name."
				style="margin-left: 6px"
				@mousedown.prevent
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="removeGraph"
				color="orange"
				icon="delete"
				iconConfirm="check"
				confirmColor="red"
				tooltipTitle="Delete mission"
				tooltipDesc="Deletes this mission and every node in it."
				style="margin-left: 6px"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="openJoin"
				label="Join To"
				color="green"
				icon="call_merge"
				labelConfirm=""
				:isDisabled="graphNodeStore.graphs.length < 2"
				style="margin-left: 8px"
			/>
			<q-btn-toggle
				:model-value="graphNodeStore.viewMode"
				@update:model-value="onViewModeChange"
				toggle-color="green"
				color="grey-9"
				text-color="grey-4"
				dense
				unelevated
				no-caps
				:options="[
					{ label: 'Focus', value: 'focus', icon: 'center_focus_strong' },
					{ label: 'Map', value: 'map', icon: 'hub' },
				]"
				style="margin-left: 8px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="undo"
				color="orange"
				icon="undo"
				:isDisabled="!graphNodeStore.canUndo"
				tooltipTitle="Undo last action"
				:tooltipDesc="graphNodeStore.undoLabel"
				style="margin-left: 6px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="exportDot"
				color="green"
				icon="file_download"
				:isDisabled="!graphNodeStore.currentGraphId"
				tooltipTitle="Export mission (DOT)"
				tooltipDesc="Downloads this mission as a Graphviz .dot file."
				style="margin-left: 6px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="() => triggerImportDot('new')"
				color="green"
				icon="file_upload"
				tooltipTitle="Import mission (DOT)"
				tooltipDesc="Creates a new mission from a Graphviz .dot file."
				style="margin-left: 6px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="() => triggerImportDot('merge')"
				color="green"
				icon="merge"
				:isDisabled="!graphNodeStore.currentGraphId"
				tooltipTitle="Merge DOT into mission"
				tooltipDesc="Folds a .dot file into the current mission: updates matched nodes, adds new ones, reconciles edges."
				style="margin-left: 6px"
			/>
			<input
				ref="dotFileInput"
				type="file"
				accept=".dot,.gv,text/vnd.graphviz,text/plain"
				style="display: none"
				@change="onDotFileChosen"
			/>
			<q-separator vertical dark style="margin: 0 12px" />
			<q-input
				dark
				dense
				outlined
				v-model="editingNode.ident"
				@update:model-value="onIdentInput"
				label="Ident"
				class="sci-fi-selector"
				style="width: 80px"
			>
				<q-tooltip class="sci-fi-tooltip" :delay="500">
					Room number / number-letter combo (e.g. 2B). Becomes the journal
					entry dragged onto the Foundry map.
				</q-tooltip>
			</q-input>
			<q-input
				dark
				dense
				outlined
				v-model="editingNode.title"
				@update:model-value="onTitleInput"
				label="Name"
				class="sci-fi-selector"
				style="width: 200px; margin-left: 8px"
			/>
			<q-select
				dark
				dense
				outlined
				v-model="editingNode.type"
				emit-value
				map-options
				use-input
				input-debounce="0"
				new-value-mode="add-unique"
				@new-value="onNewLocationType"
				@update:modelValue="onTypeChanged"
				@filter="filterTypes"
				:options="typeOptions"
				label="Type"
				class="sci-fi-selector"
				style="width: 160px; margin-left: 8px"
			>
				<template #option="scope">
					<q-item v-bind="scope.itemProps">
						<q-item-section>{{ scope.opt.label }}</q-item-section>
						<q-item-section v-if="scope.opt.deletable" side>
							<q-btn dense flat round size="sm" icon="delete" color="orange" @click.stop="confirmDeleteLocation(scope.opt.value, scope.opt.label)" />
						</q-item-section>
					</q-item>
				</template>
			</q-select>
			<q-select
				dark
				dense
				outlined
				v-model="editingNode.subType"
				emit-value
				map-options
				use-input
				input-debounce="0"
				:new-value-mode="isLocationTypeSelected ? 'add-unique' : undefined"
				@new-value="onNewSubLocation"
				@update:modelValue="onSubTypeChanged"
				@filter="filterSubTypes"
				:options="subTypeOptions"
				label="Sub Type"
				class="sci-fi-selector"
				style="width: 170px; margin-left: 8px"
			>
				<template #option="scope">
					<q-item v-bind="scope.itemProps">
						<q-item-section>{{ scope.opt.label }}</q-item-section>
						<q-item-section v-if="scope.opt.deletable" side>
							<q-btn dense flat round size="sm" icon="delete" color="orange" @click.stop="confirmDeleteSubLocation(scope.opt.value, scope.opt.label)" />
						</q-item-section>
					</q-item>
				</template>
			</q-select>
			<q-select
				dark
				dense
				outlined
				v-model="editingNode.shape"
				emit-value
				map-options
				@update:modelValue="graphNodeStore.onShapeChange"
				:options="shapes"
				label="Shape"
				class="sci-fi-selector"
				style="width: 120px; margin-left: 8px"
			/>
			<q-select
				dark
				dense
				outlined
				options-dense
				v-model="editingNode.tags"
				multiple
				use-input
				use-chips
				new-value-mode="add-unique"
				input-debounce="0"
				:options="tagOptions"
				@filter="filterTags"
				@update:modelValue="graphNodeStore.onTagsChange"
				label="Tags"
				class="sci-fi-selector"
				style="width: 220px; margin-left: 8px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="moveUp"
				color="orange"
				icon="arrow_upward"
				:isDisabled="!canMoveUp"
				tooltipTitle="Move up in draw order"
				style="margin-left: 6px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="moveDown"
				color="orange"
				icon="arrow_downward"
				:isDisabled="!canMoveDown"
				tooltipTitle="Move down in draw order"
				style="margin-left: 6px"
			/>
			<ActionIcon
				:item="{}"
				:isFlat="false"
				:action="deleteNode"
				color="orange"
				icon="delete"
				iconConfirm="check"
				confirmColor="red"
				tooltipTitle="Delete node"
				style="margin-left: 6px"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="addParent"
				label="Parent"
				color="green"
				icon="add"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="openNodeJoin"
				label="Join To"
				color="green"
				icon="call_merge"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="openSetParent"
				label="Set Parent"
				color="green"
				icon="account_tree"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="openFlip"
				label="Flip"
				color="green"
				icon="swap_horiz"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="openUnjoin"
				label="Unjoin"
				color="orange"
				icon="link_off"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
			<ActionButton
				:item="{}"
				:isFlat="false"
				:action="addChild"
				label="Child"
				color="green"
				icon="add"
				labelConfirm=""
				style="margin-left: 8px"
				:isDisabled="editingNode.id == '-1'"
			/>
		</div>
		<div class="row justify-between builder-body" style="width: 100%">
			<q-card class="col sci-fi-card" style="margin-right: 32px">
				<div class="row justify-center" style="width: 100%; height: 100%">
					<TreeChartCentral
						width="100%"
						height="100%"
						:graph="graphNodes"
						:mode="graphNodeStore.viewMode"
						:onNodeClick="onNodeClick"
						:onNodeDoubleClick="onNodeDoubleClick"
						:onNodeDragEnd="onNodeDragEnd"
					/>
				</div>
			</q-card>
			<q-card class="col-4 sci-fi-card2 editor-card">
				<q-tabs v-model="tab" narrow-indicator dense align="justify">
					<q-tab class="text-orange" name="info" icon="graphic_eq" label="Info" />
					<q-tab class="text-orange" name="glyph" icon="interests" label="Glyph" />
					<q-tab class="text-orange" name="color" icon="palette" label="Color" />
				</q-tabs>

				<q-tab-panels dark v-model="tab" animated class="editor-panels">
					<q-tab-panel class="background info-tab" name="info" style="padding: 4px">
						<q-tabs v-model="infoTab" dense narrow-indicator align="justify">
							<q-tab class="text-orange" name="gm" label="GM Info" />
							<q-tab class="text-orange" name="read" label="Read Aloud" />
							<q-tab class="text-orange" name="env" label="Environment" />
							<q-tab class="text-orange" name="images" label="Images" />
						</q-tabs>
						<q-tab-panels dark v-model="infoTab" animated class="background info-panels">
							<q-tab-panel class="background info-editor-panel" name="gm">
								<EditorTipTap
									v-model="editingNode.info"
									font-size="1.55rem"
									@update:model-value="(v: string) => graphNodeStore.onInfoChange('info', v)"
									style="height: 100%; border: 1px solid grey"
								/>
							</q-tab-panel>
							<q-tab-panel class="background info-editor-panel" name="read">
								<EditorTipTap
									v-model="editingNode.readAloud"
									font-size="1.55rem"
									@update:model-value="(v: string) => graphNodeStore.onInfoChange('readAloud', v)"
									style="height: 100%; border: 1px solid grey"
								/>
							</q-tab-panel>
							<q-tab-panel class="background info-editor-panel" name="env">
								<EditorTipTap
									v-model="editingNode.environment"
									font-size="1.55rem"
									@update:model-value="(v: string) => graphNodeStore.onInfoChange('environment', v)"
									style="height: 100%; border: 1px solid grey"
								/>
							</q-tab-panel>
							<q-tab-panel class="background images-panel" name="images">
								<div class="row items-center justify-between" style="margin-bottom: 8px">
									<span class="glyph-default-label">{{ editingNode.images?.length ?? 0 }}/5 IMAGES</span>
									<q-btn
										dense
										color="green"
										icon="add"
										label="Add"
										:disable="(editingNode.images?.length ?? 0) >= 5"
										@click="addImage"
									/>
								</div>
								<div class="images-list">
									<div v-for="(img, i) in editingNode.images" :key="i" class="image-row">
										<div
											class="image-drop"
											:class="{ 'image-drag': dragImageIndex === i }"
											@dragenter.prevent="dragImageIndex = i"
											@dragover.prevent="dragImageIndex = i"
											@dragleave.prevent="dragImageIndex = null"
											@drop.prevent="onDropImage(i, $event)"
										>
											<img v-if="img.url" :src="img.url" class="image-thumb" />
											<span v-else class="image-empty">drop image<br />(&le; 5MB)</span>
											<q-btn
												v-if="img.url"
												dense flat round size="sm" icon="zoom_in" color="white"
												class="image-zoom"
												@click="openLightbox(img.url)"
											/>
											<q-spinner v-if="uploadingIndex === i" color="green" size="28px" class="image-spinner" />
										</div>
										<div class="image-meta">
											<q-input
												dark dense outlined type="textarea" autogrow
												v-model="img.prompt"
												@update:model-value="(v: string) => graphNodeStore.setNodeImagePrompt(i, v)"
												label="Visual prompt"
											/>
											<q-btn dense flat size="sm" color="orange" icon="delete" label="Remove" @click="removeImage(i)" />
										</div>
									</div>
									<div v-if="!(editingNode.images?.length)" class="glyph-placeholder">
										No images yet. Click Add (up to 5).
									</div>
								</div>
							</q-tab-panel>
						</q-tab-panels>
					</q-tab-panel>

					<q-tab-panel class="background glyph-tab" name="glyph" style="padding: 8px">
						<div class="row items-center no-wrap" style="gap: 8px; margin-bottom: 8px">
							<q-input
								dark
								dense
								outlined
								v-model="glyphSearch"
								placeholder="search glyphs…"
								class="col"
							>
								<template #append><q-icon name="search" size="16px" /></template>
							</q-input>
							<q-btn
								v-if="editingNode.glyph"
								dense
								flat
								icon="close"
								color="grey-5"
								@click="clearGlyph"
							>
								<q-tooltip>Clear glyph (fall back to the subtype default)</q-tooltip>
							</q-btn>
						</div>
						<div class="glyph-grid">
							<button
								v-for="g in visibleGlyphs"
								:key="g"
								class="glyph-tile"
								:class="{ 'glyph-tile-active': editingNode.glyph === g }"
								:title="g"
								@click="pickGlyph(g)"
							>
								<img v-if="glyphUrls[g]" :src="glyphUrls[g]" loading="lazy" />
							</button>
							<div v-if="!glyphList.length" class="glyph-placeholder">Loading glyphs…</div>
						</div>
						<div class="row items-center" style="gap: 8px; margin-top: 8px">
							<q-btn
								dense
								color="green"
								icon="bookmark_add"
								label="Save as subtype default"
								:disable="!editingNode.glyph || !editingNode.type"
								@click="saveGlyphDefault"
							>
								<q-tooltip>
									Nodes of this type/subtype without their own glyph will use it.
								</q-tooltip>
							</q-btn>
							<span v-if="currentGlyphDefault" class="glyph-default-label">
								default: {{ currentGlyphDefault }}
							</span>
						</div>

						<!-- Glyph colour: separate from the node colour. -->
						<div class="glyph-color-section">
							<div class="row items-center" style="gap: 8px; margin-bottom: 6px">
								<span class="glyph-default-label">GLYPH COLOUR</span>
								<q-btn
									v-if="editingNode.glyphColor"
									dense flat size="sm" color="grey-5" icon="close" label="use node colour"
									@click="setGlyphColor(null)"
								/>
							</div>
							<div class="swatch-rows">
								<div v-for="set in colorSwatches" :key="set.label" class="swatch-row">
									<span class="swatch-row-label">{{ set.label }}</span>
									<button
										v-for="c in set.colors"
										:key="c.value"
										class="color-swatch"
										:class="{ 'color-swatch-active': (editingNode.glyphColor || editingNode.color) === c.value }"
										:style="{ background: c.value }"
										:title="c.name"
										@click="setGlyphColor(c.value)"
									/>
								</div>
							</div>
						</div>
					</q-tab-panel>

					<q-tab-panel class="background" name="color">
						<div class="color-tab-body">
							<q-color
								no-header
								no-footer
								dark
								v-model="editingNode.color"
								@update:model-value="graphNodeStore.onColorChange"
								class="color-picker"
							/>
							<div class="swatch-rows">
								<div v-for="set in colorSwatches" :key="set.label" class="swatch-row">
									<span class="swatch-row-label">{{ set.label }}</span>
									<button
										v-for="c in set.colors"
										:key="c.value"
										class="color-swatch"
										:class="{ 'color-swatch-active': editingNode.color === c.value }"
										:style="{ background: c.value }"
										:title="c.name"
										@click="pickColor(c.value)"
									/>
								</div>
							</div>
							<div class="row items-center" style="gap: 12px">
								<q-btn
									dense
									color="green"
									icon="bookmark_add"
									label="Save as subtype default"
									:disable="!editingNode.type"
									@click="saveColorDefault"
								>
									<q-tooltip>
										New nodes of this type/subtype pick up this colour when typed.
									</q-tooltip>
								</q-btn>
								<span v-if="currentColorDefault" class="glyph-default-label">
									default:
									<span :style="{ color: currentColorDefault }">{{ currentColorDefault }}</span>
								</span>
							</div>
						</div>
					</q-tab-panel>
				</q-tab-panels>
			</q-card>
		</div>
		<DialogGraphJoin />

		<!-- Full-screen image viewer (click anywhere to close). -->
		<q-dialog v-model="lightboxOpen" maximized>
			<div class="lightbox-card" @click="lightboxOpen = false">
				<img :src="lightboxUrl" class="lightbox-img" />
			</div>
		</q-dialog>
	</div>
</template>

<script setup lang="ts">
	import { ref, reactive, computed, watch, onMounted } from 'vue';
	import { useQuasar } from 'quasar';
	import '@livereader/graphly-d3/style.css';
	import { optionTypes, optionSubType, optionShape } from '../models/graphTypes';
	import { useGraphNodeStore } from '../stores/graphNodes';
	import { GraphNodeUtil, type GraphNode, type GNode } from '../models/graphNode';
	import { Event, type Node, type Link } from '@livereader/graphly-d3';

	const graphNodeStore = useGraphNodeStore();
	const $q = useQuasar();
	
	// This seems to smash the 2 way binding on editingNode for the veu reactivity
	// Yet it's still reactive and the watches end up updating the pinia model.
	// accessing graphNodeStore.editingGraphNode.xxx directly on the elements seems to work.
	// See v-model="graphNodeStore.editingGraphNode.title"
	// const editingNode = graphNodeStore.editingGraphNode;
	const currentNode = graphNodeStore.currentGraphNode;

	const editingNode: Ref<GNode> = ref(graphNodeStore.getEditingNode);

	const graphRef: Ref<HTMLElement | null> = ref(null);
	const graphHeight: Ref<number> = ref(0);
	const graphWidth: Ref<number> = ref(0);
	const linkColor = '#00ffea';

	const tab: Ref<string> = ref('info');
	const infoTab: Ref<string> = ref('gm');
	const shapes = optionShape();

	const props = defineProps({
		width: {
			type: [String, Number],
			default: '600px',
		},
		height: {
			type: [String, Number],
			default: '400px',
		},
	});

	watch(
		() => graphNodeStore.editingGraphNode,
		async () => {
			editingNode.value = { ...graphNodeStore.getEditingNode };
		}
	);

	// ─── Type / Sub Type options (structural + editable locations) ──
	// Structural types are fixed; location categories (from Firestore) are
	// deletable. Both dropdowns support add-your-own.
	const isLocationTypeSelected = computed(() => graphNodeStore.isLocationType(editingNode.value.type));

	const allTypeOptions = computed(() => [
		...optionTypes().map((t) => ({ ...t, deletable: false })),
		...graphNodeStore.locationTypeOptions.map((t) => ({ ...t, deletable: true })),
	]);
	const typeFilter = ref('');
	const typeOptions = computed(() => {
		const needle = typeFilter.value.toLowerCase();
		return needle ? allTypeOptions.value.filter((o) => o.label.toLowerCase().includes(needle)) : allTypeOptions.value;
	});
	const filterTypes = (val: string, update: (fn: () => void) => void) => {
		update(() => { typeFilter.value = val; });
	};

	const allSubTypeOptions = computed(() => {
		const type = editingNode.value.type;
		if (!type) {
			return [] as { label: string; value: string; deletable: boolean }[];
		}
		if (graphNodeStore.isLocationType(type)) {
			return graphNodeStore.locationSubtypes(type).map((s) => ({ ...s, deletable: true }));
		}
		return optionSubType(type).map((s) => ({ ...s, deletable: false }));
	});
	const subTypeFilter = ref('');
	const subTypeOptions = computed(() => {
		const needle = subTypeFilter.value.toLowerCase();
		return needle ? allSubTypeOptions.value.filter((o) => o.label.toLowerCase().includes(needle)) : allSubTypeOptions.value;
	});
	const filterSubTypes = (val: string, update: (fn: () => void) => void) => {
		update(() => { subTypeFilter.value = val; });
	};

	// Typing a new Type name creates a location category and selects it.
	const onNewLocationType = async (val: string, done: (item?: unknown) => void) => {
		const id = await graphNodeStore.addLocation(val);
		if (id) {
			await onTypeChanged(id);
		}
		done();
	};
	// Typing a new Sub Type adds a sub-location to the current location type.
	const onNewSubLocation = async (val: string, done: (item?: unknown) => void) => {
		if (!isLocationTypeSelected.value) {
			done();
			return;
		}
		const value = await graphNodeStore.addSubLocation(editingNode.value.type, val);
		if (value) {
			await onSubTypeChanged(value);
		}
		done();
	};

	const confirmDeleteLocation = (value: string, label: string) => {
		$q.dialog({
			title: 'Delete location type',
			message: `Delete "${label}" and all its sub-locations from the locations list? Nodes already using it keep their value.`,
			cancel: true,
			dark: true,
		}).onOk(async () => {
			await graphNodeStore.deleteLocation(value);
		});
	};
	const confirmDeleteSubLocation = (value: string, label: string) => {
		$q.dialog({
			title: 'Delete sub-location',
			message: `Delete "${label}" from this location's sub-locations?`,
			cancel: true,
			dark: true,
		}).onOk(async () => {
			await graphNodeStore.deleteSubLocation(editingNode.value.type, value);
		});
	};

	const graphNodes = computed(() => {
		return graphNodeStore.viewMode === 'map'
			? graphNodeStore.getMapGraph
			: graphNodeStore.getCurrentGraph;
	});

	// Focus <-> Map toggle. Entering Map loads the whole reachable graph.
	const onViewModeChange = async (mode: 'focus' | 'map') => {
		graphNodeStore.setViewMode(mode);
		if (mode === 'map') {
			await graphNodeStore.loadFullGraph();
		}
	};

	// Persist a dragged node's position (Map mode only; TreeChartCentral
	// already re-pinned the live node before calling this).
	const onNodeDragEnd = (_e: Event, node: Node, position: { x: number; y: number }) => {
		graphNodeStore.setNodePosition(node.id, position.x, position.y);
	};

	// ─── Mission (graph) selection / CRUD ─────────────────────────
	const graphFilter = ref('');
	const graphInput = ref('');

	const selectedGraphId = computed({
		get: () => graphNodeStore.currentGraphId,
		set: (graphId: string) => {
			if (graphId) {
				graphNodeStore.selectGraph(graphId);
			}
		},
	});

	const graphOptions = computed(() => {
		const needle = graphFilter.value.toLowerCase();
		if (!needle) {
			return graphNodeStore.graphs;
		}
		return graphNodeStore.graphs.filter((g) => g.name.toLowerCase().includes(needle));
	});

	const filterGraphs = (val: string, update: (fn: () => void) => void) => {
		update(() => {
			graphFilter.value = val;
		});
	};

	const onGraphInput = (val: string) => {
		graphInput.value = val;
	};

	// Enter on a name that matches no mission creates one.
	const onNewGraph = async (val: string, done: (item?: any) => void) => {
		const name = val.trim();
		if (name && !graphNodeStore.graphs.some((g) => g.name === name)) {
			await graphNodeStore.createGraph(name);
		}
		done();
	};

	const currentGraphName = computed(() => {
		return graphNodeStore.graphs.find((g) => g.id === graphNodeStore.currentGraphId)?.name ?? '';
	});

	const canRenameGraph = computed(() => {
		const name = graphInput.value.trim();
		return name.length > 0 && currentGraphName.value.length > 0 && name !== currentGraphName.value;
	});

	const saveGraphName = async () => {
		await graphNodeStore.renameGraph(graphNodeStore.currentGraphId, graphInput.value);
		graphFilter.value = '';
	};

	const canCreateGraph = computed(() => {
		const name = graphInput.value.trim();
		return name.length > 0 && !graphNodeStore.graphs.some((g) => g.name === name);
	});

	const createGraphFromInput = async () => {
		const name = graphInput.value.trim();
		if (!name) {
			return;
		}
		await graphNodeStore.createGraph(name);
		graphFilter.value = '';
	};

	const removeGraph = async () => {
		await graphNodeStore.deleteGraph(graphNodeStore.currentGraphId);
	};

	const openJoin = () => {
		graphNodeStore.openJoinDialog('join');
	};

	const undo = async () => {
		await graphNodeStore.undo();
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};

	// ─── DOT export / import ──────────────────────────────────────
	const dotFileInput = ref<HTMLInputElement | null>(null);
	const pendingImportMode = ref<'new' | 'merge'>('new');

	const exportDot = async () => {
		const result = await graphNodeStore.exportCurrentGraphDot();
		if (!result) {
			$q.notify({ type: 'warning', message: 'Select a mission to export.' });
			return;
		}
		const slug = result.name.trim().replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'mission';
		const blob = new Blob([result.dot], { type: 'text/vnd.graphviz' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${slug}.dot`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const triggerImportDot = (mode: 'new' | 'merge') => {
		pendingImportMode.value = mode;
		dotFileInput.value?.click();
	};

	const onDotFileChosen = async (e: globalThis.Event) => {
		const input = e.target as HTMLInputElement;
		const file = input.files?.[0];
		// Reset so choosing the same file again re-fires change.
		input.value = '';
		if (!file) {
			return;
		}
		let text: string;
		try {
			text = await file.text();
		} catch (err: any) {
			$q.notify({ type: 'negative', message: `Could not read file: ${err?.message ?? err}` });
			return;
		}
		const mode = pendingImportMode.value;
		const result = await graphNodeStore.importGraphFromDot(text, mode);
		if (!result.ok) {
			$q.notify({ type: 'negative', message: `${mode === 'merge' ? 'Merge' : 'Import'} failed: ${result.error}` });
			return;
		}
		editingNode.value = { ...graphNodeStore.getEditingNode };
		const msg =
			mode === 'merge'
				? `Merged into "${result.name}" (${result.count} nodes, ${result.added} new).`
				: `Imported "${result.name}" (${result.count} nodes).`;
		$q.notify({ type: 'positive', message: msg });
	};

	// Link the selected node under another node in the tree.
	const openNodeJoin = () => {
		graphNodeStore.openJoinDialog('node', editingNode.value.id);
	};

	// Reassign the selected node's parent to another node in the tree.
	const openSetParent = () => {
		graphNodeStore.openJoinDialog('setparent', editingNode.value.id);
	};

	// Reverse the arrow between the selected node and a connected node.
	const openFlip = () => {
		graphNodeStore.openJoinDialog('flip', editingNode.value.id);
	};

	// Remove the link between the selected node and a connected node.
	const openUnjoin = () => {
		graphNodeStore.openJoinDialog('unjoin', editingNode.value.id);
	};

	// Deleting a node can orphan subtrees; surface the Join/Delete/As-new-graph
	// dialog for each orphan root, one at a time.
	watch(
		[() => graphNodeStore.pendingOrphans, () => graphNodeStore.joinDialog.open],
		([orphans, open]) => {
			if (orphans.length > 0 && !open) {
				graphNodeStore.openJoinDialog('orphan', orphans[0]);
			}
		}
	);

	// Draw-order position of the editing node within the centre node's
	// parents or children array (the arcs render in array order).
	const moveContext = computed(() => {
		const center = graphNodeStore.findLocalNode(graphNodeStore.currentGraphNode);
		const id = editingNode.value.id;
		if (!center || id === '-1' || id === center.id) {
			return null;
		}
		for (const list of [center.children, center.parents]) {
			const index = list.indexOf(id);
			if (index !== -1) {
				return { index, length: list.length };
			}
		}
		return null;
	});
	const canMoveUp = computed(() => moveContext.value != null && moveContext.value.index > 0);
	const canMoveDown = computed(() => moveContext.value != null && moveContext.value.index < moveContext.value.length - 1);

	// ─── Glyph picker (Itemizer glyph archive) ────────────────────
	const GLYPH_RENDER_CAP = 120;
	const glyphList = ref<string[]>([]);
	const glyphSearch = ref('');
	const glyphUrls = reactive<Record<string, string>>({});

	const filteredGlyphs = computed(() => {
		const needle = glyphSearch.value.trim().toLowerCase();
		return needle ? glyphList.value.filter((g) => g.toLowerCase().includes(needle)) : glyphList.value;
	});
	const visibleGlyphs = computed(() => filteredGlyphs.value.slice(0, GLYPH_RENDER_CAP));

	// Mint object URLs lazily for whatever tiles are visible.
	watch(
		visibleGlyphs,
		async (names) => {
			for (const name of names) {
				if (!glyphUrls[name]) {
					const url = await glyphObjectUrl(name);
					if (url) {
						glyphUrls[name] = url;
					}
				}
			}
		},
		{ immediate: true }
	);

	const pickGlyph = async (glyphName: string) => {
		await graphNodeStore.onGlyphChange(glyphName);
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};

	// Separate glyph colour (null = use node colour).
	const setGlyphColor = (color: string | null) => {
		editingNode.value.glyphColor = color;
		graphNodeStore.onGlyphColorChange(color);
	};

	const clearGlyph = async () => {
		await graphNodeStore.onGlyphChange('');
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};

	const saveGlyphDefault = async () => {
		await graphNodeStore.saveGlyphDefault();
		await graphNodeStore.ensureGlyphsForNodes();
	};

	const currentGlyphDefault = computed(() => {
		const node = editingNode.value;
		if (!node.type) {
			return '';
		}
		return (
			graphNodeStore.glyphDefaults[`${node.type}:${node.subType}`] ||
			graphNodeStore.glyphDefaults[node.type] ||
			''
		);
	});

	// New nodes (or freshly loaded defaults) may reference glyphs that aren't
	// cached yet; top the cache up whenever the node set grows.
	watch(
		() => graphNodeStore.nodes.length,
		() => {
			graphNodeStore.ensureGlyphsForNodes();
		}
	);

	// Tag cloud suggestions for the tags select (most-used first).
	const tagFilter = ref('');
	const tagOptions = computed(() => {
		const cloud = graphNodeStore.getTagCloud;
		const needle = tagFilter.value.toLowerCase();
		return needle ? cloud.filter((t) => t.toLowerCase().includes(needle)) : cloud;
	});
	const filterTags = (val: string, update: (fn: () => void) => void) => {
		update(() => {
			tagFilter.value = val;
		});
	};

	const deleteNode = async () => {
		await graphNodeStore.deleteNode();
	};

	// ─── Node reference images (Info > Images) ────────────────────
	const dragImageIndex = ref<number | null>(null);
	const uploadingIndex = ref<number | null>(null);
	const lightboxOpen = ref(false);
	const lightboxUrl = ref('');
	const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

	const addImage = () => {
		graphNodeStore.addNodeImage();
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};
	const removeImage = (index: number) => {
		graphNodeStore.removeNodeImage(index);
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};
	const openLightbox = (url: string) => {
		lightboxUrl.value = url;
		lightboxOpen.value = true;
	};
	const onDropImage = async (index: number, e: DragEvent) => {
		dragImageIndex.value = null;
		const file = e.dataTransfer?.files?.[0];
		if (!file) {
			return;
		}
		if (!file.type.startsWith('image/')) {
			$q.notify({ type: 'warning', message: 'Drop an image file.' });
			return;
		}
		if (file.size > MAX_IMAGE_BYTES) {
			$q.notify({ type: 'negative', message: 'Image exceeds the 5MB limit.' });
			return;
		}
		uploadingIndex.value = index;
		try {
			const webp = await blobToWebp(file, 1600);
			const url = await uploadGraphImage(webp);
			graphNodeStore.setNodeImageUrl(index, url);
			editingNode.value = { ...graphNodeStore.getEditingNode };
		} catch (err: any) {
			console.error('Image upload failed:', err);
			$q.notify({ type: 'negative', message: `Upload failed: ${err?.message ?? err}` });
		} finally {
			uploadingIndex.value = null;
		}
	};

	// Changing the type auto-defaults the shape (type2Shape), so the local
	// panel copy must re-sync or the shape select keeps showing the old value
	// while the graph draws the new one.
	// Name/Ident drive the graph label, so writing to the store on every
	// keystroke re-rendered the d3 graph and made typing jerky. Debounce the
	// store write (graph updates shortly after you pause); the input field is
	// bound to the local editingNode copy, so it stays instant.
	let labelTimer: ReturnType<typeof setTimeout> | null = null;
	const onTitleInput = (v: string) => {
		if (labelTimer) clearTimeout(labelTimer);
		labelTimer = setTimeout(() => graphNodeStore.onTitleChange(v), 300);
	};
	const onIdentInput = (v: string) => {
		if (labelTimer) clearTimeout(labelTimer);
		labelTimer = setTimeout(() => graphNodeStore.onIdentChange(v), 300);
	};

	const onTypeChanged = async (newType: string) => {
		await graphNodeStore.onTypeChange(newType);
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};

	// Subtype change can apply a colour default; re-sync the panel copy.
	const onSubTypeChanged = async (newSubtype: string) => {
		await graphNodeStore.onSubTypeChange(newSubtype);
		editingNode.value = { ...graphNodeStore.getEditingNode };
	};

	const saveColorDefault = async () => {
		await graphNodeStore.saveColorDefault();
	};

	// Quick-pick swatches: the site's UI palette plus the GMG tactical-map
	// legend colours (mission-design.md: atmosphere / radiation / gravity /
	// temperature / lighting states).
	const colorSwatches = [
		{
			label: 'Cybercore',
			colors: [
				{ name: 'Cyber Cyan (links/accents)', value: '#00ffea' },
				{ name: 'Terminal Green (editors)', value: '#12e049' },
				{ name: 'Node Green (graph default)', value: '#51ff00' },
				{ name: 'Alert Orange (headings)', value: '#ff6600' },
				{ name: 'Neon Purple (card glow)', value: '#8f83fa' },
				{ name: 'Glow Blue (card border)', value: '#5e4cff' },
				{ name: 'Magenta', value: '#ff00dd' },
				{ name: 'White', value: '#ffffff' },
			],
		},
		{
			label: 'GMG Legend',
			colors: [
				{ name: 'Toxic Yellow (toxic atmosphere)', value: '#ffd633' },
				{ name: 'Vacuum Red (vacuum / extreme heat)', value: '#ff3344' },
				{ name: 'Decompression Orange', value: '#ff8800' },
				{ name: 'Radiation Green', value: '#21d630' },
				{ name: 'Ice Cyan (extreme cold)', value: '#88fff5' },
				{ name: 'Low-G Blue', value: '#3388ff' },
				{ name: 'Zero-G Purple', value: '#bbaaff' },
				{ name: 'Dim Grey (dim lighting)', value: '#9a9a9a' },
			],
		},
	];

	const pickColor = (value: string) => {
		editingNode.value.color = value;
		graphNodeStore.onColorChange(value);
	};

	const currentColorDefault = computed(() => {
		const node = editingNode.value;
		if (!node.type) {
			return '';
		}
		return (
			graphNodeStore.colorDefaults[`${node.type}:${node.subType}`] ||
			graphNodeStore.colorDefaults[node.type] ||
			''
		);
	});

	const moveUp = async () => {
		await graphNodeStore.moveEditingNode(-1);
	};

	const moveDown = async () => {
		await graphNodeStore.moveEditingNode(1);
	};

	const addParent = async () => {
		await graphNodeStore.addParent();
	};

	const addChild = async () => {
		await graphNodeStore.addChild();
	};

	const onNodeClick = async (e: Event, node: Node) => {
		await graphNodeStore.setEditNode(node.id);
	};

	const onNodeDoubleClick = async (e: Event, node: Node) => {
		await graphNodeStore.setCurrentNode(node.id);
	};

	onMounted(async () => {
		// Glyph archive loads independently of graph data, so a Firestore
		// hiccup can't leave the picker stuck on "Loading glyphs…".
		glyphNames()
			.then((names) => {
				glyphList.value = names;
			})
			.catch((error) => console.error('Failed to load glyph archive:', error));
		// loadGraphs hits Firestore and (when empty) tries to create a graph,
		// which fails for unauthenticated users. Catch it so a permission error
		// can't reject onMounted and bounce the route away to the default page.
		try {
			await graphNodeStore.loadGraphs();
		} catch (error) {
			console.error('Graph Builder: failed to load graphs (are you signed in?):', error);
		}
		editingNode.value = { ...graphNodeStore.getEditingNode };
	});
</script>

<style scoped>
	.color-tab-body {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		padding-top: 8px;
	}

	.color-picker {
		width: 100%;
		max-width: 420px;
	}

	.background {
		background: linear-gradient(135deg, #1c1c1c, #000000);
	}

	.sci-fi-card {
		background: linear-gradient(135deg, #1c1c1c, #000000);
		border: 1px solid #5e4cff;
		box-shadow: 0 0 8px 5px #8f83fa;
		border-radius: 16px;
		overflow: hidden;
		position: relative;
	}

	.sci-fi-card2 {
		background: linear-gradient(135deg, #1c1c1c, #000000);
		color: hsl(175, 100%, 50%);
		border: 1px solid #0f5c00;
		box-shadow: 0 0 8px 5px #7fffa6;
		border-radius: 16px;
		overflow: hidden;
		position: relative;
	}

	.sci-fi-card:hover {
		border-color: #8f83fa; /* Replace 'neonblue' with the actual neon blue color code */
		box-shadow: 0 0 8px 5px #8f83fa; /* Replace 'neonblue' with the actual neon blue color code */
	}

	.sci-fi-card-header {
		font-size: var(--fs-xlarge);
		font-weight: bold;
		text-align: center;
		background-color: rgba(0, 0, 0, 0.8);
	}

	.sci-fi-card-content {
		padding: 15px;
		background-color: rgba(0, 0, 0, 0.5);
	}

	.sci-fi-card-footer {
		background-color: rgba(0, 0, 0, 0.8);
	}

	.sci-fi-selector {
		background: linear-gradient(135deg, #1c1c1c, #000000);
		color: hsl(175, 100%, 50%);
		overflow: hidden;
		margin-bottom: 8px;
	}
	.sci-fi-card-email {
		background: linear-gradient(135deg, #1c1c1c, #000000);
		color: hsl(175, 100%, 50%);
		overflow: hidden;
	}

	.sci-fi-card,
	.sci-fi-card-header,
	.sci-fi-card-content,
	.sci-fi-card-footer {
		user-select: none;
		cursor: default;
	}

	.clickable {
		cursor: pointer;
	}

	.info-editor-panel {
		padding: 8px 4px 4px 4px;
	}

	/* ─── full-height layout ───────────────────────────────────────── */
	.graph-tree-builder {
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	.mission-bar {
		flex: 0 0 auto;
	}

	.builder-body {
		flex: 1 1 auto;
		min-height: 0;
	}

	.editor-card {
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	.editor-panels {
		flex: 1 1 auto;
		min-height: 0;
	}

	.info-tab {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.info-panels {
		flex: 1 1 auto;
		min-height: 0;
	}

	/* ─── glyph picker ─────────────────────────────────────────────── */
	.glyph-tab {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.glyph-grid {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
		gap: 4px;
		align-content: start;
	}

	.glyph-tile {
		aspect-ratio: 1;
		background: #0a1418;
		border: 1px solid #1f3a33;
		border-radius: 4px;
		cursor: pointer;
		padding: 4px;
	}

	.glyph-tile img {
		width: 100%;
		height: 100%;
	}

	.glyph-tile:hover {
		border-color: #12e049;
	}

	.glyph-tile-active {
		border-color: #12e049;
		box-shadow: 0 0 6px #12e04980;
	}

	.glyph-placeholder {
		color: #9a9a9a;
		padding: 16px;
	}

	.glyph-default-label {
		color: #9a9a9a;
		font-size: var(--fs-tiny);
	}

	/* ─── colour swatch rows ───────────────────────────────────────── */
	.swatch-rows {
		display: flex;
		flex-direction: column;
		gap: 10px;
		width: 100%;
		max-width: 420px;
	}

	.swatch-row {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.swatch-row-label {
		font-size: var(--fs-tiny);
		color: #999;
		width: 86px;
		flex-shrink: 0;
		letter-spacing: 0.4px;
		text-transform: uppercase;
	}

	.color-swatch {
		width: 26px;
		height: 26px;
		border-radius: 4px;
		border: 1px solid #333;
		cursor: pointer;
		padding: 0;
	}

	.color-swatch:hover {
		transform: scale(1.18);
	}

	.color-swatch-active {
		outline: 2px solid #fff;
		outline-offset: 1px;
	}

	.glyph-color-section {
		margin-top: 12px;
		padding-top: 10px;
		border-top: 1px solid #1f3a33;
	}

	/* ─── images panel ─────────────────────────────────────────────── */
	.images-panel {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 8px;
	}

	.images-list {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.image-row {
		display: flex;
		gap: 10px;
	}

	.image-drop {
		position: relative;
		width: 120px;
		height: 120px;
		flex-shrink: 0;
		border: 1px dashed #1f3a33;
		border-radius: 6px;
		background: #0a1418;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}

	.image-drag {
		border-color: #12e049;
		box-shadow: 0 0 6px #12e04980;
	}

	.image-thumb {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.image-empty {
		color: #9a9a9a;
		font-size: var(--fs-tiny);
		text-align: center;
	}

	.image-zoom {
		position: absolute;
		top: 2px;
		right: 2px;
		background: rgba(0, 0, 0, 0.5);
	}

	.image-spinner {
		position: absolute;
	}

	.image-meta {
		flex: 1 1 auto;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.lightbox-card {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.9);
		cursor: zoom-out;
	}

	.lightbox-img {
		max-width: 95vw;
		max-height: 95vh;
		object-fit: contain;
	}
</style>
